import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { login as apiLogin, getMe, loginByCode as apiLoginByCode, loginWithFamilyCode as apiLoginFamily } from "../lib/api";

const AuthContext = createContext(null);

// School levels configuration
export const NIVEAUX_SCOLAIRES = [
  { id: "gs", name: "Grande Section", shortName: "GS", order: 0 },
  { id: "cp", name: "CP", shortName: "CP", order: 1 },
  { id: "ce1", name: "CE1", shortName: "CE1", order: 2 },
  { id: "ce2", name: "CE2", shortName: "CE2", order: 3 },
  { id: "cm1", name: "CM1", shortName: "CM1", order: 4 },
  { id: "cm2", name: "CM2", shortName: "CM2", order: 5 },
];

// Available roles
export const ROLES = {
  TEACHER: "teacher",
  STUDENT: "student",
  PARENT: "parent",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const userId = localStorage.getItem("navi_user_id");
    const userType = localStorage.getItem("navi_user_type"); // "teacher", "student", or "parent"
    
    if (!userId) {
      setLoading(false);
      return;
    }
    
    // For code-based login, restore from localStorage
    if (userType === "student" || userType === "parent") {
      const storedUser = {
        id: userId,
        name: localStorage.getItem("navi_user_name"),
        role: localStorage.getItem("navi_user_role"),
        niveau: localStorage.getItem("navi_user_niveau"),
        type: userType,
      };
      if (userType === "parent") {
        storedUser.student_id = localStorage.getItem("navi_student_id");
        storedUser.student_name = localStorage.getItem("navi_student_name");
      }
      setUser(storedUser);
      setLoading(false);
      return;
    }
    
    // For teachers, verify with backend
    try {
      const res = await getMe();
      const userData = { ...res.data, type: "teacher" };
      setUser(userData);
    } catch {
      clearLocalStorage();
    }
    setLoading(false);
  }, []);

  const clearLocalStorage = () => {
    localStorage.removeItem("navi_user_id");
    localStorage.removeItem("navi_user_name");
    localStorage.removeItem("navi_user_role");
    localStorage.removeItem("navi_user_niveau");
    localStorage.removeItem("navi_user_type");
    localStorage.removeItem("navi_student_id");
    localStorage.removeItem("navi_student_name");
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Teacher login
  const login = async (name, role, niveau = null) => {
    const res = await apiLogin(name, role, niveau);
    const userData = { ...res.data, type: "teacher" };
    localStorage.setItem("navi_user_id", userData.id);
    localStorage.setItem("navi_user_name", userData.name);
    localStorage.setItem("navi_user_role", userData.role);
    localStorage.setItem("navi_user_type", "teacher");
    if (userData.niveau) {
      localStorage.setItem("navi_user_niveau", userData.niveau);
    }
    setUser(userData);
    return userData;
  };

  // Student/Parent login by code
  const loginWithCode = async (accessCode) => {
    const res = await apiLoginByCode(accessCode);
    const userData = res.data;
    
    localStorage.setItem("navi_user_id", userData.id);
    localStorage.setItem("navi_user_name", userData.name);
    localStorage.setItem("navi_user_role", userData.role);
    localStorage.setItem("navi_user_type", userData.type);
    
    if (userData.niveau) {
      localStorage.setItem("navi_user_niveau", userData.niveau);
    }
    if (userData.type === "parent") {
      localStorage.setItem("navi_student_id", userData.student_id);
      localStorage.setItem("navi_student_name", userData.student_name);
    }
    
    setUser(userData);
    return userData;
  };

  // Parent login with family code (ABC-123-XYZ)
  const loginWithFamilyCode = async (code) => {
    const res = await apiLoginFamily(code);
    // Backend returns: { user_id, student_name, role }
    const responseData = res.data;
    
    const userData = {
      id: responseData.user_id,
      name: responseData.student_name,
      role: responseData.role || "parent",
      type: "parent",
      student_name: responseData.student_name,
    };
    
    localStorage.setItem("navi_user_id", userData.id);
    localStorage.setItem("navi_user_name", userData.name);
    localStorage.setItem("navi_user_role", "parent");
    localStorage.setItem("navi_user_type", "parent");
    localStorage.setItem("navi_student_name", responseData.student_name);
    
    setUser(userData);
    return { user: userData, student_name: responseData.student_name };
  };

  const logout = () => {
    clearLocalStorage();
    setUser(null);
  };

  const updateUserPreferences = (prefs) => {
    if (prefs.niveau) {
      localStorage.setItem("navi_user_niveau", prefs.niveau);
      setUser(prev => ({ ...prev, niveau: prefs.niveau }));
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login,
      loginWithCode,
      loginWithFamilyCode,
      logout, 
      updateUserPreferences,
      NIVEAUX_SCOLAIRES,
      ROLES 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
